import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { CheckIcon, LinkExternalIcon } from '@persona-id/design-tokens/icons';
import { motion } from 'framer-motion';
import styled, { ThemeProvider } from 'styled-components';

import Divider from 'dashboard/components/Divider';
import { FormRegisterEmail, FormRegisterEmailData } from 'dashboard/forms/FormRegisterEmail';
import { FormRegisterFull, FormRegisterFullData } from 'dashboard/forms/FormRegisterFull';
import {
  FormRegisterTrialEssential,
  FormRegisterTrialEssentialData,
} from 'dashboard/forms/FormRegisterTrialEssential';
import Spacer from 'design-system/components/Spacer';
import { Display, Heading, Paragraph } from 'design-system/components/Text';
import { marketingFontTheme } from 'design-system/theme';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import LogoLight from 'images/brand/logo-color-light.svg';
import AgeImage from 'images/dashboard/onboarding/age.svg';
import DocumentImage from 'images/dashboard/onboarding/document.svg';
import GovIdImage from 'images/dashboard/onboarding/gov-id.svg';
import PepImage from 'images/dashboard/onboarding/pep.svg';
import PhoneImage from 'images/dashboard/onboarding/phone-2fa.svg';
import SelfieImage from 'images/dashboard/onboarding/selfie.svg';
import SsnImage from 'images/dashboard/onboarding/ssn.svg';
import WatchlistImage from 'images/dashboard/onboarding/watchlist.svg';

interface DashboardRegisterViewProps {
  savedEmail: string;
  emailStepCompleted: boolean;
  fullFormStepCompleted: boolean;
  createOnboardingFlow: (values: FormRegisterEmailData) => void;
  updateOnboardingFlow: (values: FormRegisterFullData) => void;
  updateOnboardingFlowWithTrialEssentialInfo: (values: FormRegisterTrialEssentialData) => void;
  skipTrialEssential: () => void;
  setEmailStepCompleted: (emailStepCompleted: boolean) => void;
  setFullFormStepCompleted: (fullFormStepCompleted: boolean) => void;
}

const DashboardRegisterView = ({
  savedEmail,
  emailStepCompleted,
  fullFormStepCompleted,
  createOnboardingFlow,
  updateOnboardingFlow,
  updateOnboardingFlowWithTrialEssentialInfo,
  skipTrialEssential,
  setEmailStepCompleted,
}: DashboardRegisterViewProps) => {
  let currentStep;
  if (emailStepCompleted && !fullFormStepCompleted) {
    currentStep = (
      <FormRegisterFull
        savedEmail={savedEmail}
        setEmailStepCompleted={setEmailStepCompleted}
        submit={updateOnboardingFlow}
      />
    );
  } else if (emailStepCompleted && fullFormStepCompleted) {
    currentStep = (
      <FormRegisterTrialEssential
        submit={updateOnboardingFlowWithTrialEssentialInfo}
        skipTrialEssential={skipTrialEssential}
      />
    );
  } else {
    currentStep = <FormRegisterEmail savedEmail={savedEmail} submit={createOnboardingFlow} />;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
      <ThemeProvider theme={marketingFontTheme}>
        <PageContainer>
          <Logo />
          <Divider full />
          <ContentContainer>
            <Spacer height={4} />
            <GridContainer>
              <Column>
                <Display size='sm'>
                  {emailStepCompleted && fullFormStepCompleted ? 'Good news!' : 'Let’s get started'}
                </Display>
                <Spacer height={1} />
                {currentStep}
              </Column>
              {emailStepCompleted && fullFormStepCompleted ? (
                <Column>
                  <PlanFeaturesContainer>
                    <PlanFeaturesGridHeaders>
                      <PlanFeatureGridHeaderContainer key='coverage-table-header'>
                        <PlanFeatureGridHeader></PlanFeatureGridHeader>
                        <PlanFeatureGridHeader>
                          <Paragraph size='md' weight='bold'>
                            Trial with real data in Production
                          </Paragraph>
                        </PlanFeatureGridHeader>
                        <PlanFeatureGridHeader>
                          <Paragraph size='md' weight='bold'>
                            Trial with mocked sample data in Sandbox
                          </Paragraph>
                        </PlanFeatureGridHeader>
                      </PlanFeatureGridHeaderContainer>
                    </PlanFeaturesGridHeaders>
                    <PlanFeaturesRow>
                      <PlanFeatureCell>
                        <Paragraph size='md'>
                          Access to Essential plan features, including 20+ preconfigured solutions
                        </Paragraph>
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                    </PlanFeaturesRow>
                    <PlanFeaturesRow>
                      <PlanFeatureCell>
                        <Paragraph size='md'>60 day trial period</Paragraph>
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                    </PlanFeaturesRow>
                    <PlanFeaturesRow>
                      <PlanFeatureCell>
                        <Paragraph size='md'>Full integration suite</Paragraph>
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                    </PlanFeaturesRow>
                    <PlanFeaturesRow>
                      <PlanFeatureCell>
                        <Paragraph size='md'>
                          Unlimited mocked verification services in Sandbox
                        </Paragraph>
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                    </PlanFeaturesRow>
                    <PlanFeaturesRow>
                      <PlanFeatureCell>
                        <Paragraph size='md'>50 free verification services in Production</Paragraph>
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                      <PlanFeatureCell></PlanFeatureCell>
                    </PlanFeaturesRow>
                    <PlanFeaturesRow>
                      <PlanFeatureCell>
                        <Paragraph size='md'>End-to-end integration testing</Paragraph>
                      </PlanFeatureCell>
                      <PlanFeatureCell>
                        <CheckIcon />
                      </PlanFeatureCell>
                      <PlanFeatureCell></PlanFeatureCell>
                    </PlanFeaturesRow>
                  </PlanFeaturesContainer>
                  <ReviewAllPlansLink>
                    <a href='https://withpersona.com/pricing' target='_blank' rel='noreferrer'>
                      Review all plans
                    </a>
                    <LinkExternalIcon />
                  </ReviewAllPlansLink>
                </Column>
              ) : (
                <Column>
                  <Heading size='sm'>Explore our most used building blocks in your Sandbox</Heading>
                  <Spacer height={2} />
                  <Paragraph>
                    Learn the basics with pre-built solutions or craft your own identity program
                    with modular functionality.
                  </Paragraph>
                  <Spacer height={2} />
                  <BuildingBlocksContainer>
                    <BuildingBlocksRow>
                      <PhoneImage />
                      <PepImage />
                      <WatchlistImage />
                      <SelfieImage />
                    </BuildingBlocksRow>
                    <BuildingBlocksRow>
                      <SsnImage />
                      <GovIdImage />
                      <DocumentImage />
                      <AgeImage />
                    </BuildingBlocksRow>
                  </BuildingBlocksContainer>
                </Column>
              )}
            </GridContainer>
          </ContentContainer>
          <Footer>
            <Paragraph>© 2023 Persona Identities Inc.</Paragraph>
          </Footer>
        </PageContainer>
      </ThemeProvider>
    </motion.div>
  );
};

const PlanFeaturesRow = styled('div')`
  display: grid;
  grid-template-columns: 40% 30% 30%;
  padding: ${Spacing[2]} ${Spacing[2]};
  width: 100%;
  color: ${Color.PrimaryBlack};
  ${Typography.TextMedium}
`;

const PlanFeaturesContainer = styled('div')`
  margin-bottom: ${Spacing[2]};
`;

const PlanFeaturesGridHeaders = styled('div')`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const PlanFeatureGridHeaderContainer = styled(PlanFeaturesRow)`
  background: ${Color.CoreSlate100};
`;

const PlanFeatureGridHeader = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: ${Spacing[1]} 0px ${Spacing[1]} 0px;
  text-align: center;
  font-weight: ${FontWeight.Bold};
`;

const PlanFeatureCell = styled('div')`
  width: 100%;
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  padding: ${Spacing[1]} 0px ${Spacing[1]} 0px;
  white-space: normal;
  align-items: center;

  svg {
    margin: 0 auto;
    display: block;
  }
`;

const BuildingBlocksContainer = styled('div')`
  margin-bottom: ${Spacing[2]};
  text-align: center;
`;

const BuildingBlocksRow = styled('div')`
  display: inline-flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};

  svg {
    padding: ${Spacing[1]} 0px ${Spacing[1]} 0px;
  }
`;

const PageContainer = styled('div')`
  position: relative;
  background-color: ${Color.PrimaryWhite};
  width: 100%;
  height: 100%;
  min-height: 100vh;
`;

const Logo = styled(LogoLight)`
  width: 128px;
  height: auto;
  margin-top: ${Spacing[4]};
  margin-left: ${Spacing[5]};
  margin-bottom: -${Spacing[2]};
`;

const ContentContainer = styled('div')`
  max-width: 1400px;
  margin: auto;
  padding-top: ${Spacing[7]};
  padding-bottom: ${BASE * 10}px;
  padding-left: ${Spacing[5]};
  padding-right: ${Spacing[5]};
`;

const GridContainer = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${BASE * 16}px;
`;

const Column = styled('div')`
  flex: 1;
  min-width: 350px;
  padding-bottom: ${Spacing[7]};
`;

const ReviewAllPlansLink = styled('div')`
  float: right;
  color: ${Color.CorePurple700};
  display: flex;
  align-items: center;

  a {
    flex: 1;
    ${Typography.TextMediumLight};
    display: block;
    text-decoration: underline;
    padding: 5px;
  }
`;

const Footer = styled('footer')`
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: ${Color.CoreSlate100};
  padding: ${Spacing[5]} 0px ${Spacing[5]} ${Spacing[5]};
`;

export default DashboardRegisterView;
